<template>
  <div class="w-full h-screen max-h-screen flex flex-col main-container">
    <div class="flex-1 flex h-full min-h-0">
      <div
        class="w-52 pl-2"
        id="toggle-sidebar"
        :class="!isOpenSidebar ? 'close-sidebar' : ''"
      >
        <figure class="sidebar-logo">
          <a href="/"><img src="@/assets/main-logo.png" class="img-fluid" /></a>
        </figure>
        <div
          :key="overallCompKey"
          v-if="!isComponentView"
          class="h-1/2 overflow-auto ml-2"
        >
          <ComponentTree
            :components="components"
            :currentComponent="currentComponent"
            :loading="loading"
            @openAdd="openAdd"
            @select:component="selectComponent"
            @remove:dep="handleRemoveDep"
          />
        </div>
        <div class="h-1/2 overflow-auto ml-2">
          <ComponentList
            :key="overallCompKey"
            :components="components"
            :currentComponent="currentComponent"
            :loading="loading"
            :isComponentView="isComponentView"
            :isEditable="isEditable"
            @select:component="selectComponent"
            @setDeps="setDeps"
            @remove="handleRemoveComponent"
            @openAdd="openAdd"
          />
        </div>
      </div>
      <div
        class="w-full h-screen overflow-hidden"
        id="wrapper-content"
        :class="!isOpenSidebar ? 'ml-0' : 'content-wrapper-margin'"
      >
        <SiteEditPageHeader
          :key="overallCompKey"
          :site="site"
          :components="components"
          :versionData="versionData"
          :showSettings="!isComponentView"
          :selectedVersion="selectedVersion"
          :showImport="!isComponentView && !!siteId"
          :hasPreviousState="hasPreviousState"
          :revertToPreviousState="revertToPreviousState"
          :hasMultiplePreviousStates="hasMultiplePreviousStates"
          :revertAllPreviousStates="revertAllPreviousStates"
          :getAllPreviousStatesCount="getAllPreviousStatesCount"
          :isReverting="isReverting"
          @update="updateSiteSettings"
          @save="handleSave"
          @updateURI="updateURI"
          @deleteSite="deleteSite"
          @selectVersion="selectVersion"
          @addNewVersion="addNewVersion"
          @setMetaVersion="setMetaVersion"
          @deleteVersion="deleteVersion"
          @importVersion="openImportVersionModal"
          @toggleSidebar="toggleSidebar"
        />
        <div class="component-wrapper">
          <div class="flex h-full" style="height: calc(100vh - 40px)">
            <!-- Left Side: Component Settings + Template Editor -->
            <div
              class="flex-1 flex flex-col"
              :style="{
                width: isAIPanelOpen
                  ? `calc(100% - ${aiPanelWidth}px - 3px)`
                  : '100%',
              }"
            >
              <div
                v-if="!isComponentView && currentComponent && !isEditable"
                class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4"
              >
                <div>
                  <div class="flex items-center">
                    <div class="flex-shrink-0">
                      <div>
                        <img
                          src="@/assets/component-warning.svg"
                          alt="Warning"
                          class="h-4 w-4"
                        />
                      </div>
                    </div>
                    <div class="ml-3">
                      <p class="text-sm text-yellow-700">
                        This is a global component. You can only view this
                        component.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="mx-3 mt-3"
                :class="{
                  'mx-3': !isEditable,
                }"
              >
                <SiteSelfSettings
                  v-if="currentComponent"
                  :currentComponent="currentComponent"
                  :isComponentView="isComponentView"
                  :isEditable="isEditable"
                  :previousStates="getCurrentPreviousStates()"
                  :isReverting="isReverting"
                  @update:settings="setSettings"
                  @set="setProps"
                  @update="handleSourceUpdate"
                  @remove="handleRemoveDataSource"
                  @add="handleAddDataSource"
                  @addMethod="handleAddMethod"
                  @updateMethod="handleUpdateMethod"
                  @removeMethod="handleRemoveMethod"
                  @addComputed="handleAddComputed"
                  @updateComputed="handleUpdateComputed"
                  @removeComputed="handleRemoveComputed"
                  @addWatch="handleAddWatch"
                  @updateWatch="handleUpdateWatch"
                  @removeWatch="handleRemoveWatch"
                  @selectVersion="selectCurrentComponentVersion"
                  @revertState="revertToPreviousState"
                />
              </div>

              <div
                v-if="currentComponent"
                class="p-3 component-container flex-1"
                style="min-height: 0"
              >
                <div class="w-full mb-8 h-full">
                  <div class="theme-toggle-container">
                    <!-- Revert Template Button -->
                    <button
                      v-if="hasPreviousState('template')"
                      @click="revertToPreviousState('template')"
                      class="revert-template-btn"
                      :disabled="isReverting('template')"
                      title="Revert to previous template"
                    >
                      <i class="fas fa-undo"></i>
                    </button>
                    <label class="theme-toggle">
                      <input
                        type="checkbox"
                        @change="toggleTheme"
                        :checked="currentTheme === 'vs-dark'"
                      />
                      <span class="slider">
                        <span class="icon sun">☀︎</span>
                        <span class="icon moon">⏾</span>
                      </span>
                    </label>
                  </div>

                  <div
                    class="h-100"
                    :key="currentComponent._id"
                    style="height: calc(100% - 40px)"
                  >
                    <TemplateEditor
                      v-if="currentComponent"
                      :template="currentComponent.template"
                      :currentTheme="currentTheme"
                      :isEditable="isEditable"
                      @set="updateTemplate"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Resize Handle -->
            <div
              v-if="isAIPanelOpen"
              class="ai-resize-handle"
              @mousedown="startResize"
            >
              <div class="ai-resize-handle-line"></div>
            </div>

            <!-- AI Panel (Right) - Full Height (only when open) -->
            <div
              v-if="isAIPanelOpen"
              class="ai-panel-wrapper"
              :style="{ width: aiPanelWidth + 'px', height: '100%' }"
            >
              <AIPromptModal
                :isOpen="isAIPanelOpen"
                :generationType="'method'"
                :componentContext="currentComponent"
                :siteId="siteId"
                :uri="uri"
                :globalCss="site.globalCss || ''"
                :hasMultiplePreviousStates="hasMultiplePreviousStates"
                :revertAllPreviousStates="revertAllPreviousStates"
                :isReverting="isReverting"
                @toggle="toggleAIPanel"
                @close="closeAIPanel"
                @error="handleAIError"
                @insertCode="handleInsertCode"
                @createComponent="handleCreateComponent"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <CreateSiteModel
      v-if="!loading && !isComponentView && !siteId"
      :uri="uri"
      @save="createNewSite"
      @create-global="createGlobalComponent"
    />
    <ImportVersionModal
      v-if="showImportVersionModal"
      :isVisible="showImportVersionModal"
      :uri="uri"
      :versionData="versionData"
      @update:isVisible="showImportVersionModal = $event"
      @close="showImportVersionModal = false"
      @imported="handleVersionImported"
    />
    <AddComponent
      ref="addCompRef"
      :components="components"
      :nodeId="nodeId"
      @new="addComponent"
      @addDep="addDep"
    />
    <!-- AI Floating Button (only when panel is closed) -->
    <AIPromptModal
      v-if="!isAIPanelOpen"
      :isOpen="false"
      :generationType="'method'"
      :componentContext="currentComponent"
      :siteId="siteId"
      :uri="uri"
      :globalCss="site.globalCss || ''"
      :hasMultiplePreviousStates="hasMultiplePreviousStates"
      :revertAllPreviousStates="revertAllPreviousStates"
      :isReverting="isReverting"
      @toggle="toggleAIPanel"
      @close="closeAIPanel"
      @error="handleAIError"
      @insertCode="handleInsertCode"
    />
  </div>
</template>
<script>
import * as siteService from "../service/site";
import * as compService from "../service/component";
import * as aiRevertService from "../service/aiRevert";
import { syncSiteAuthLoginComponent } from "../service/siteAuthLogin";
import { validateHipaaConfigForSave } from "../utilities/validateHipaaButtonClass.js";

import SiteEditPageHeader from "../components/SiteEditPageHeader.vue";
import ComponentTree from "../components/ComponentTree.vue";
import ComponentList from "../components/ComponentList.vue";
import TemplateEditor from "../components/TemplateEditor.vue";
import AddComponent from "../components/AddComponent.vue";
import CreateSiteModel from "./components/CreateSiteModal.vue";
import SiteSelfSettings from "../components/SiteSelfSettings.vue";
import AIPromptModal from "../components/AIPromptModal.vue";
import ImportVersionModal from "../components/ImportVersionModal.vue";

export default {
  name: "SiteEditor",
  props: {
    isComponentView: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SiteEditPageHeader,
    ComponentList,
    TemplateEditor,
    CreateSiteModel,
    ComponentTree,
    AddComponent,
    SiteSelfSettings,
    AIPromptModal,
    ImportVersionModal,
  },
  computed: {
    currentComponent() {
      return this.selectedIndex !== -1
        ? this.components[this.selectedIndex]
        : null;
    },
    previewurl() {
      return "//";
    },
    isEditable() {
      return (
        this.isComponentView ||
        (this.currentComponent &&
          (!this.currentComponent.is_global ||
            !!this.forceEdit[this.currentComponent?._id]))
      );
    },
    selectedIndex() {
      return this.components.findIndex((cp) => {
        return cp.old_id === this.selectedCId;
      });
    },
    overallCompKey() {
      return this.components.map((cp) => cp._id).join("-");
    },
  },
  watch: {
    isComponentView() {
      this.load();
    },
    // Load previous states when current component changes
    currentComponent: {
      handler(newComponent) {
        if (newComponent && newComponent._id) {
          this.refreshPreviousStates(newComponent._id);
        }
      },
      immediate: true,
    },
  },
  data: () => {
    return {
      loading: true,
      siteId: null,
      uri: "",
      versionData: {
        versions: [],
        published_version: "1.0",
        current_version: "1.0",
      },
      site: {
        name: "",
        siteTitle: "",
        extFiles: [],
      },
      selectedCId: -1,
      components: [],
      compPage: {
        page: 0,
        perPage: 20,
        count: 0,
      },
      forceEdit: {},
      nodeId: null,
      selectedVersion: "",
      showImportVersionModal: false,
      isOpenSidebar: true,
      currentTheme: "vs-dark",
      isAIPanelOpen: false,
      aiPanelWidth: 380,
      isResizing: false,
      // Previous states for AI undo functionality (from API)
      previousStates: {},
      sitePreviousStates: {},
      // Track revert operations in progress
      reverting: {
        type: null,
        name: null,
        all: false,
      },
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.site = {
        name: "loading...",
      };
      if (this.isComponentView) {
        this.site = {
          name: "Manage global components",
        };
        await this.loadGCompos();
      } else {
        await this.loadSitesData();
      }
      this.loading = false;
    },
    async loadGCompos() {
      const res = await compService.getGlobalComponents(
        this.compPage.page,
        this.compPage.perPage,
      );
      this.components = res.components;
      this.compPage.count = res.count;
    },
    openAdd(nodeId) {
      this.nodeId = nodeId;
      this.$refs.addCompRef.showModal = true;
    },
    addDep(compId) {
      const pIdx = this.components.findIndex((c) => c._id === this.nodeId);
      this.components[pIdx].deps.push(compId);
    },
    async handleRemoveDep({ nodeId, parentId }) {
      const pIdx = this.components.findIndex((c) => c._id === parentId);
      const dIdx = this.components[pIdx].deps.findIndex((c) => c === nodeId);
      this.components[pIdx].deps.splice(dIdx, 1);
    },
    async handleRemoveComponent(id) {
      const cidx = this.components.findIndex((c) => c._id === id);
      this.components.splice(cidx, 1);
      await compService.updateComponent(id, { is_deleted: true });
      const len = this.components.length;
      for (let i = 0; i < len; i++) {
        const comp = this.components[i];
        const didx = comp.deps.findIndex((did) => did === id);
        comp.deps.splice(didx, 1);
      }
      this.saveSiteData();
    },
    async makeGCompCopy() {
      this.components[this.selectedIndex].is_global = false;
      this.components[this.selectedIndex]._id = null;
    },
    async refreshVersionList() {
      const { uri } = this.$route.params;
      try {
        const versionData = await siteService.getSiteVersions(uri);
        this.versionData = versionData;
      } catch (err) {
        // non-fatal: keep stale list rather than disrupt the editor
      }
    },
    async loadSitesData(version) {
      const { uri } = this.$route.params;
      this.uri = uri;

      // New-site flow: site doesn't exist yet, so skip version fetch.
      // After saveSiteData() persists the site, this runs again with siteId set and picks up the version.
      const isCreating = this.$route?.query?.create === 'true' && !this.siteId;
      if (isCreating) {
        await this.createNewSite();
        await this.refreshPreviousStates();
      }

      const versionData = await siteService.getSiteVersions(uri);
      this.selectedVersion = version || versionData.current_version;
      const res = await siteService.getSiteWithVersion(
        uri,
        version || versionData.current_version,
      );
      this.versionData = versionData;
      if (res && res._id) {
        this.siteId = res._id;
        this.components = res.components.map((c) => {
          return {
            methods: [],
            computed: [],
            watch: [],
            ...c,
            old_id: c._id,
          };
        });
        this.site = {
          ...res,
          ...res.settings,
          extFiles: res.ext_files,
          hipaaConfig: res.hipaa_config,
        };
        if (!this.site.name) {
          this.site.name = uri;
        }
      }
      await this.refreshPreviousStates();
    },
    async handleSave(resolve, reject) {
      try {
        if (this.isComponentView) {
          const comp = this.currentComponent;
          await compService.updateComponent(comp._id, { component: comp });
        } else {
          const hipaaError = validateHipaaConfigForSave(
            this.site.hipaaConfig,
            this.components,
          );
          if (hipaaError) {
            throw new Error(hipaaError);
          }

          await this.saveSiteData();
        }
        this.forceEdit = {};
        this.$notify("Saved.");
        if (typeof resolve === "function") {
          resolve();
        }
      } catch (err) {
        this.$notify({
          type: "warn",
          text: err.message || String(err),
        });
        if (typeof reject === "function") {
          reject(err);
        }
      }
    },
    async createNewSite() {
      this.site.name = this.uri;
      await this.addComponent({
        template: `<div>app component template</div>`,
        name: "app",
        props: [],
        data: [],
        methods: [],
        computed: [],
        watch: [],
        deps: [],
      });
      await this.saveSiteData();

      // After creating site, remove the create query param from the URL
      try {
        const { uri } = this.$route.params;
        this.$router.replace({ name: 'SiteEditor', params: { uri }, query: {} });
      } catch (e) {
        // ignore router errors
      }
    },
    async saveSiteData() {
      if (!this.isComponentView && this.site) {
        const loginSync = await syncSiteAuthLoginComponent({
          site: this.site,
          components: this.components,
        });
        if (loginSync.created) {
          this.$notify({
            type: "info",
            text: "Global login component attached to this site.",
          });
        }
      }

      const idMap = {};
      await Promise.all(
        this.components
          .filter((c) => !c._id)
          .map(async (comp) => {
            const newComp = await compService.createComponent({
              component: comp,
            });
            idMap[comp.old_id] = newComp._id;
            comp._id = newComp._id;
            comp.old_id = newComp._id;
          }),
      );
      this.components.forEach((comp) => {
        comp.deps = comp.deps.map((dep) => idMap[dep] || dep);
      });
      const processedComponents = await Promise.all(
        this.components
          .filter((c) => c._id)
          .map(async (comp) => {
            return await compService.updateComponent(comp._id, {
              component: comp,
            });
          }),
      );
      const extraAuths = (this.site.extraAuths || []).filter(a => a.key && a.url);
      const data = {
        site: {
          uri: this.uri,
          components: processedComponents.map((comp) => comp._id),
          settings: {
            ...this.site,
            extraAuths,
            extFiles: undefined,
            hipaaConfig: undefined,
          },
          ext_files: this.site.extFiles,
          hipaa_config: this.site.hipaaConfig,
        },
      };
      if (this.siteId) {
        await siteService.updateSite(this.siteId, data);
      } else {
        const res = await siteService.createSite(data);
        this.siteId = res._id;
      }
      await this.loadSitesData(this.selectedVersion);
      // this.$refs.previewRef.reloadIframe()
    },
    selectComponent(id) {
      this.selectedCId = id;
    },
    async addComponent(comp) {
      if (comp._id) {
        this.components.push(comp);
      } else {
        const newComp = await compService.createComponent({
          component: {
            ...comp,
            is_global: comp.is_global || this.isComponentView,
          },
        });
        this.components.push({
          ...newComp,
          old_id: newComp._id,
        });
        if (this.nodeId) {
          const idx = this.components.findIndex((c) => c._id === this.nodeId);
          this.components[idx].deps = (this.components[idx].deps || []).concat(
            newComp._id,
          );
          this.nodeId = null;
        }
      }
    },
    updateTemplate(code) {
      this.components[this.selectedIndex].template = code;
    },
    setProps(list) {
      this.components[this.selectedIndex].props = list;
    },
    setDeps(list) {
      this.components[this.selectedIndex].deps = list;
    },
    setSettings(settings) {
      if (
        !this.components[this.selectedIndex].is_global &&
        settings.is_global
      ) {
        this.forceEdit[this.currentComponent._id] = true;
      }
      Object.keys(settings).forEach((field) => {
        this.components[this.selectedIndex][field] = settings[field];
      });
    },
    handleSourceUpdate({ idx, data }) {
      this.components[this.selectedIndex].data[idx] = data;
    },
    handleRemoveDataSource(idx) {
      this.components[this.selectedIndex].data.splice(idx, 1);
    },
    handleAddDataSource({ name, type }) {
      let remoteItem = {};
      if (type === "remote") {
        remoteItem = {
          method: "GET",
          resolve: {
            section: "res",
            url_params: [],
            headers: [],
          },
        };
      }
      this.components[this.selectedIndex].data.push({
        key: name,
        type,
        ...remoteItem,
        default_value: null,
      });
    },
    handleAddMethod({ name }) {
      this.components[this.selectedIndex].methods.push({
        name,
        body: "",
      });
    },
    handleUpdateMethod({ key, index, value }) {
      switch (key) {
        case "name":
          this.components[this.selectedIndex].methods[index]["name"] = value;
          break;
        case "body":
          this.components[this.selectedIndex].methods[index]["body"] = value;
          break;
      }
    },
    handleRemoveMethod({ index }) {
      this.components[this.selectedIndex].methods.splice(index, 1);
    },
    handleAddComputed({ name }) {
      this.components[this.selectedIndex].computed.push({
        name,
        body: "",
      });
    },
    handleUpdateComputed({ key, index, value }) {
      switch (key) {
        case "name":
          this.components[this.selectedIndex].computed[index]["name"] = value;
          break;
        case "body":
          this.components[this.selectedIndex].computed[index]["body"] = value;
          break;
      }
    },
    handleRemoveComputed({ index }) {
      this.components[this.selectedIndex].computed.splice(index, 1);
    },
    handleAddWatch({ name }) {
      this.components[this.selectedIndex].watch.push({
        name,
        body: "",
      });
    },
    handleUpdateWatch({ key, index, value }) {
      switch (key) {
        case "name":
          this.components[this.selectedIndex].watch[index]["name"] = value;
          break;
        case "body":
          this.components[this.selectedIndex].watch[index]["body"] = value;
          break;
      }
    },
    handleRemoveWatch({ index }) {
      this.components[this.selectedIndex].watch.splice(index, 1);
    },
    updateSiteSettings(settings) {
      this.site = {
        ...this.site,
        ...settings,
      };
    },
    async createGlobalComponent() {
      const { uri } = this.$route.params;
      await compService.createComponent({
        component: {
          template: `<div>${uri} component template</div>`,
          name: uri,
          props: [],
          data: [],
          deps: [],
          is_global: true,
        },
      });
      // Navigate to global components editor with a full page reload
      window.location.href = '/global-components'
    },
    async updateURI() {
      const newUri = window.prompt(
        "New Site URI for " + this.$route.params.uri,
      );
      if (!newUri) {
        return;
      }
      try {
        const res = await siteService.updateSite(this.siteId, {
          site: { uri: newUri },
        });
        if (res.error) {
          this.$notify({
            type: "warn",
            text: `${res.error}`,
          });
          return;
        }
        this.$router.push({
          name: "SiteEditor",
          params: { uri: newUri },
        });
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } catch (err) {
        this.$notify({
          type: "warn",
          text: `${err}`,
        });
      }
    },
    async deleteSite() {
      try {
        if (window.confirm("Are you sure?")) {
          const res = await siteService.deleteSite(this.siteId);
          if (res.error) {
            this.$notify({
              type: "warn",
              text: `${res.error}`,
            });
            return;
          }
          this.$router.push({
            name: "SitesList",
          });
        }
      } catch (err) {
        this.$notify({
          type: "warn",
          text: `${err}`,
        });
      }
    },
    selectVersion(version) {
      this.loadSitesData(version);
    },
    async addNewVersion({ newVersion, note, onSuccess, onError } = {}) {
      try {
        await siteService.updateSiteVersion(
          this.uri,
          this.selectedVersion,
          newVersion,
          note,
        );
        await this.loadSitesData(newVersion);
        this.$notify({
          type: "success",
          text: `Version ${newVersion} created successfully`,
        });
        if (onSuccess) onSuccess();
      } catch (err) {
        this.$notify({ type: "warn", text: err.message || `${err}` });
        if (onError) onError(err);
      }
    },
    openImportVersionModal() {
      this.showImportVersionModal = true;
    },
    async handleVersionImported(payload) {
      this.showImportVersionModal = false;
      // Prefer landing on the version the user just made current; otherwise the
      // first freshly imported version; otherwise just refresh the current view.
      const imported = (payload && payload.importedVersions) || [];
      const target =
        (payload && payload.setCurrent) || imported[0] || this.selectedVersion;
      await this.loadSitesData(target);
    },
    async setMetaVersion(metaVersion) {
      await siteService.setSiteVersion(this.uri, {
        ...this.versionData,
        ...metaVersion,
      });
      this.loadSitesData(this.selectedVersion);
    },
    async deleteVersion(payload) {
      const version = typeof payload === 'string' ? payload : payload?.version;
      const deleteNote = typeof payload === 'object' ? payload.deleteNote || '' : '';
      const skipReload = typeof payload === 'object' ? !!payload.skipReload : false;
      const onSuccess = typeof payload === 'object' ? payload.onSuccess : null;
      const onError = typeof payload === 'object' ? payload.onError : null;
      try {
        await siteService.deleteSiteVersion(this.uri, version, deleteNote);
        if (skipReload) {
          await this.refreshVersionList();
        } else {
          await this.loadSitesData();
        }
        this.$notify({
          type: "success",
          text: `Version ${version} deleted successfully`,
        });
        if (onSuccess) onSuccess();
      } catch (err) {
        this.$notify({ type: "warn", text: err.message || `${err}` });
        if (onError) onError(err);
      }
    },
    toggleSidebar() {
      this.isOpenSidebar = !this.isOpenSidebar;
    },
    toggleTheme() {
      this.currentTheme = this.currentTheme === "vs-dark" ? "vs" : "vs-dark";
    },
    async selectCurrentComponentVersion(version) {
      if (this.currentComponent.version !== version && !this.loading) {
        this.loading = true;
        const res = await compService.getComponentByVersion(
          this.currentComponent.versionModel,
          version,
        );
        this.components[this.selectedIndex] = {
          ...this.currentComponent,
          ...res[0],
        };
        this.loading = false;
      }
    },
    handleAIError(error) {
      // Handle AI generation errors
      console.error("AI Error:", error);
      this.$notify({
        type: "error",
        text: `AI Error: ${error.error || "Unknown error occurred"}`,
      });
    },
    async handleInsertCode({
      type,
      name,
      code,
      action,
      target,
      onSuccess,
      onError,
    }) {
      // Handle CSS insertion separately (goes to global CSS, not component)
      if (type === "css") {
        // Check if CSS code is empty
        if (!code || !code.trim()) {
          this.$notify({
            type: "warning",
            text: "No CSS code to insert",
          });
          if (onError) onError();
          return;
        }
        // Backend handles merging - frontend just needs action and code
        await this.insertCSSIntoGlobal(code, action, onSuccess, onError);
        return;
      }

      // Handle template - AI always returns complete template
      if (type === "template") {
        // Check if a component is selected
        if (!this.currentComponent) {
          // Ask user to select a component
          const componentNames = this.components
            .map((c, i) => `${i + 1}. ${c.name || "Unnamed Component"}`)
            .join("\n");
          const selection = window.prompt(
            `Please select a component to update the template:\n\n${componentNames}\n\nEnter the component number:`,
          );

          if (!selection) {
            if (onError) onError();
            return;
          }

          const index = parseInt(selection) - 1;
          if (isNaN(index) || index < 0 || index >= this.components.length) {
            alert("Invalid selection. Please try again.");
            if (onError) onError();
            return;
          }

          // Select the component and retry
          this.selectedCId = this.components[index].old_id;
          this.$nextTick(() => {
            this.handleInsertCode({
              type,
              name,
              code,
              action,
              target,
              onSuccess,
              onError,
            });
          });
          return;
        }

        try {
          // Save previous template state only if template already exists (not new)
          const currentTemplate = this.currentComponent.template || "";
          if (currentTemplate.trim()) {
            // Only save if there's existing template content (not empty)
            await this.savePreviousState("template", null, currentTemplate);
          }

          // AI always returns complete template, so just set it directly
          this.updateTemplate(code);

          // Show success notification
          this.$notify({
            type: "success",
            text: `Template replaced successfully!`,
          });

          if (onSuccess) onSuccess();
        } catch (error) {
          console.error("Error updating template:", error);
          this.$notify({
            type: "error",
            text: `Failed to update template: ${error.message}`,
          });
          if (onError) onError();
        }
        return;
      }

      // Check if a component is selected
      if (!this.currentComponent) {
        // Ask user to select a component
        const componentNames = this.components
          .map((c, i) => `${i + 1}. ${c.name || "Unnamed Component"}`)
          .join("\n");
        const selection = window.prompt(
          `Please select a component to insert the code into:\n\n${componentNames}\n\nEnter the component number:`,
        );

        if (!selection) {
          if (onError) onError();
          return; // User cancelled
        }

        const index = parseInt(selection) - 1;
        if (isNaN(index) || index < 0 || index >= this.components.length) {
          alert("Invalid selection. Please try again.");
          if (onError) onError();
          return;
        }

        // Select the component
        this.selectedCId = this.components[index].old_id;

        // Wait for component selection to take effect
        this.$nextTick(async () => {
          await this.insertCodeIntoComponent(
            type,
            name,
            code,
            action,
            onSuccess,
            onError,
          );
        });
      } else {
        await this.insertCodeIntoComponent(
          type,
          name,
          code,
          action,
          onSuccess,
          onError,
        );
      }
    },
    async handleCreateComponent({ component, components, onSuccess, onError }) {
      try {
        // Check if component with this name already exists
        const existingComponent = this.components.find(
          (c) => c.name === component.name,
        );
        if (existingComponent) {
          alert(
            `Component "${component.name}" already exists. Please choose a different name or delete the existing component first.`,
          );
          if (onError) onError();
          return;
        }

        // Create the new component
        const newComponent = {
          name: component.name,
          template: component.template,
          is_global: component.is_global || false,
          summary: component.summary || "",
          props: component.props || [],
          data: [],
          methods: [],
          computed: [],
          watch: [],
          deps: component.deps || [],
          created_by_ai: true,
        };

        // Add component to the list
        await this.addComponent(newComponent);

        // Find the newly created component and select it
        const createdComp = this.components.find(
          (c) => c.name === component.name,
        );
        if (createdComp) {
          this.selectedCId = createdComp.old_id;

          // Wait for component to be selected
          await this.$nextTick();

          // Insert all components into the newly created component
          if (components && components.length > 0) {
            for (const comp of components) {
              const { type, name, code, action } = comp;

              // Insert CSS into global styles
              if (type === "css") {
                await new Promise((resolve) => {
                  this.insertCSSIntoGlobal(
                    code,
                    action || "append",
                    () => {
                      comp.inserted = true;
                      resolve();
                    },
                    resolve,
                  );
                });
                continue;
              }

              // Insert into component
              await new Promise((resolve) => {
                this.insertCodeIntoComponent(
                  type === "watcher" ? "watch" : type,
                  name,
                  code,
                  action || "add",
                  () => {
                    // Mark component as inserted in the metadata
                    comp.inserted = true;
                    resolve();
                  },
                  resolve,
                );
              });
            }
          }

          // Show success notification
          this.$notify({
            type: "success",
            text: `Component "${component.name}" created successfully with all components!`,
          });

          // Call onSuccess which will update the UI to hide insert buttons
          if (onSuccess) onSuccess();
        } else {
          throw new Error("Failed to find created component");
        }
      } catch (error) {
        console.error("Error creating component:", error);
        alert(`Failed to create component: ${error.message}`);
        if (onError) onError();
      }
    },
    async insertCodeIntoComponent(
      type,
      name,
      code,
      action,
      onSuccess,
      onError,
    ) {
      if (this.selectedIndex === -1 || !this.components[this.selectedIndex]) {
        alert("No component selected. Please try again.");
        if (onError) onError();
        return;
      }

      const component = this.components[this.selectedIndex];

      // Helper to normalize method/computed/watch names (remove async for comparison)
      const normalizeName = (methodName) => {
        if (!methodName) return "";
        return methodName.replace(/^\s*async\s+/, "").trim();
      };

      // Default action to 'add' if not specified
      const actionType = action || "add";

      try {
        // Check for duplicates
        let exists = false;
        let existingIndex = -1;

        if (type === "method") {
          const normalizedName = normalizeName(name);
          existingIndex = component.methods
            ? component.methods.findIndex(
                (m) => normalizeName(m.name) === normalizedName,
              )
            : -1;
          exists = existingIndex !== -1;

          if (exists && actionType === "add") {
            alert(
              `Method "${name}" already exists in this component. Please rename it or delete the existing one first.`,
            );
            if (onError) onError();
            return;
          }

          if (actionType === "replace") {
            if (!exists) {
              alert(`Method "${name}" does not exist. Cannot replace.`);
              if (onError) onError();
              return;
            }
            // Save previous state before replacing (both name and body to preserve async keyword)
            const previousMethod = component.methods[existingIndex];
            const previousState = JSON.stringify({
              name: previousMethod.name,
              body: previousMethod.body,
            });
            await this.savePreviousState("method", name, previousState);
            // Replace existing method (both name and body if name changed)
            if (previousMethod.name !== name) {
              this.handleUpdateMethod({
                key: "name",
                index: existingIndex,
                value: name,
              });
            }
            this.handleUpdateMethod({
              key: "body",
              index: existingIndex,
              value: code,
            });
          } else {
            // Add new method
            this.handleAddMethod({ name });
            const newIndex = component.methods.length - 1;
            this.handleUpdateMethod({
              key: "body",
              index: newIndex,
              value: code,
            });
          }
        } else if (type === "computed") {
          const normalizedName = normalizeName(name);
          existingIndex = component.computed
            ? component.computed.findIndex(
                (c) => normalizeName(c.name) === normalizedName,
              )
            : -1;
          exists = existingIndex !== -1;

          if (exists && actionType === "add") {
            alert(
              `Computed property "${name}" already exists in this component. Please rename it or delete the existing one first.`,
            );
            if (onError) onError();
            return;
          }

          if (actionType === "replace") {
            if (!exists) {
              alert(
                `Computed property "${name}" does not exist. Cannot replace.`,
              );
              if (onError) onError();
              return;
            }
            // Save previous state before replacing (both name and body to preserve async keyword)
            const previousComputed = component.computed[existingIndex];
            const previousState = JSON.stringify({
              name: previousComputed.name,
              body: previousComputed.body,
            });
            await this.savePreviousState("computed", name, previousState);
            // Replace existing computed (both name and body if name changed)
            if (previousComputed.name !== name) {
              this.handleUpdateComputed({
                key: "name",
                index: existingIndex,
                value: name,
              });
            }
            this.handleUpdateComputed({
              key: "body",
              index: existingIndex,
              value: code,
            });
          } else {
            // Add new computed
            this.handleAddComputed({ name });
            const newIndex = component.computed.length - 1;
            this.handleUpdateComputed({
              key: "body",
              index: newIndex,
              value: code,
            });
          }
        } else if (type === "watch") {
          const normalizedName = normalizeName(name);
          existingIndex = component.watch
            ? component.watch.findIndex(
                (w) => normalizeName(w.name) === normalizedName,
              )
            : -1;
          exists = existingIndex !== -1;

          if (exists && actionType === "add") {
            alert(
              `Watcher "${name}" already exists in this component. Please rename it or delete the existing one first.`,
            );
            if (onError) onError();
            return;
          }

          if (actionType === "replace") {
            if (!exists) {
              alert(`Watcher "${name}" does not exist. Cannot replace.`);
              if (onError) onError();
              return;
            }
            // Save previous state before replacing (both name and body to preserve async keyword)
            const previousWatch = component.watch[existingIndex];
            const previousState = JSON.stringify({
              name: previousWatch.name,
              body: previousWatch.body,
            });
            await this.savePreviousState("watch", name, previousState);
            // Replace existing watch (both name and body if name changed)
            if (previousWatch.name !== name) {
              this.handleUpdateWatch({
                key: "name",
                index: existingIndex,
                value: name,
              });
            }
            this.handleUpdateWatch({
              key: "body",
              index: existingIndex,
              value: code,
            });
          } else {
            // Add new watch
            this.handleAddWatch({ name });
            const newIndex = component.watch.length - 1;
            this.handleUpdateWatch({
              key: "body",
              index: newIndex,
              value: code,
            });
          }
        } else if (type === "data") {
          existingIndex = component.data
            ? component.data.findIndex((d) => d.key === name)
            : -1;
          exists = existingIndex !== -1;

          if (exists && actionType === "add") {
            alert(
              `Data property "${name}" already exists in this component. Please rename it or delete the existing one first.`,
            );
            if (onError) onError();
            return;
          }

          if (actionType === "replace") {
            if (!exists) {
              alert(`Data property "${name}" does not exist. Cannot replace.`);
              if (onError) onError();
              return;
            }
            // Save previous state before replacing
            await this.savePreviousState(
              "data",
              name,
              component.data[existingIndex].default_value,
            );
            // Replace existing data property value - parse the value for AI-created components
            const parsedValue = siteService.parseDataValue(code);
            component.data[existingIndex].default_value = parsedValue;
          } else {
            // Add new data property (as const type)
            this.handleAddDataSource({ name, type: "const" });
            const newIndex = component.data.length - 1;
            // Parse the value for AI-created components
            const parsedValue = siteService.parseDataValue(code);
            component.data[newIndex].default_value = parsedValue;
          }
        }

        // Show success notification
        const actionLabel = actionType === "replace" ? "replaced" : "inserted";
        this.$notify({
          type: "success",
          text: `${type.charAt(0).toUpperCase() + type.slice(1)} "${name}" ${actionLabel} successfully!`,
        });

        // Call success callback
        if (onSuccess) onSuccess();
      } catch (error) {
        console.error("Error inserting/replacing code:", error);
        alert(`Failed to ${actionType} ${type}: ${error.message}`);
        if (onError) onError();
      }
    },
    /**
     * Insert CSS into global styles
     * Backend handles smart merging - frontend just replaces or appends
     * @param {string} code - CSS code from backend (already merged if replace_all)
     * @param {string} action - 'replace_all' (backend merged) or 'append' (new CSS)
     * @param {Function} onSuccess - Success callback
     * @param {Function} onError - Error callback
     */
    async insertCSSIntoGlobal(code, action, onSuccess, onError) {
      try {
        // Initialize global CSS if empty
        if (!this.site.globalCss) {
          this.site.globalCss = "";
        }

        // Save previous CSS state only if CSS already exists (not new)
        const previousCss = this.site.globalCss || "";
        if (previousCss.trim()) {
          // Only save if there's existing CSS content (not empty)
          await this.savePreviousState("globalCss", null, previousCss);
        }

        // Backend logic: replace_all = already merged, append = new CSS
        const actionType = action || "append";
        let newCss = "";

        if (actionType === "replace_all") {
          // Backend has already merged existing + new CSS
          // Just replace entirely with the merged result
          newCss = code;
        } else {
          // Append new CSS to existing
          newCss = this.site.globalCss.trim()
            ? this.site.globalCss + "\n\n" + code
            : code;
        }

        // Update global CSS
        this.site.globalCss = newCss;

        // Show success notification
        const actionLabel =
          actionType === "replace_all" ? "replaced" : "inserted";
        this.$notify({
          type: "success",
          text: `CSS ${actionLabel} successfully!`,
        });

        if (onSuccess) onSuccess();
      } catch (error) {
        console.error("Error inserting CSS:", error);
        this.$notify({
          type: "error",
          text: `Failed to update CSS: ${error.message}`,
        });
        if (onError) onError();
      }
    },
    getDefaultAIMode() {
      // Return default mode based on context
      // For now, default to 'chat'
      // Can be enhanced later to detect current tab
      return "chat";
    },
    toggleAIPanel() {
      this.isAIPanelOpen = !this.isAIPanelOpen;
    },
    closeAIPanel() {
      this.isAIPanelOpen = false;
    },
    startResize(event) {
      this.isResizing = true;
      this.initialMouseX = event.clientX;
      this.initialPanelWidth = this.aiPanelWidth;
      window.addEventListener("mousemove", this.resizePanel);
      window.addEventListener("mouseup", this.stopResize);
      event.preventDefault();
    },
    resizePanel(event) {
      if (this.isResizing) {
        const deltaX = this.initialMouseX - event.clientX; // Inverted because panel is on right
        const newWidth = this.initialPanelWidth + deltaX;
        // Min width 250px, max width 50% of container
        const maxWidth = window.innerWidth * 0.5;
        this.aiPanelWidth = Math.max(250, Math.min(newWidth, maxWidth));
      }
    },
    stopResize() {
      this.isResizing = false;
      window.removeEventListener("mousemove", this.resizePanel);
      window.removeEventListener("mouseup", this.stopResize);
    },
    // =============================================
    // Previous State Management (AI Undo Feature)
    // =============================================

    /**
     * Refresh previous states from API for current component + site
     */
    async refreshPreviousStates(componentIdOverride = null) {
      if (!this.siteId) return;

      const componentId =
        componentIdOverride ||
        (this.currentComponent ? this.currentComponent._id : null);

      try {
        const response = await aiRevertService.fetchPreviousStates({
          siteId: this.siteId,
          componentId,
          uri: this.uri,
        });

        const data = response && response.result ? response.result : response;
        const statesRoot = data && data.states ? data.states : data;
        const componentStates =
          statesRoot && (statesRoot.componentStates || statesRoot.component)
            ? statesRoot.componentStates || statesRoot.component
            : {};
        let siteStates =
          statesRoot && (statesRoot.siteStates || statesRoot.site)
            ? statesRoot.siteStates || statesRoot.site
            : {};
        if (
          (!siteStates || Object.keys(siteStates).length === 0) &&
          statesRoot &&
          statesRoot.globalCss
        ) {
          siteStates = { globalCss: statesRoot.globalCss };
        }

        if (componentId) {
          this.previousStates[componentId] = componentStates || {};
        }
        this.sitePreviousStates = siteStates || {};
      } catch (error) {
        console.error("Error loading previous states from API:", error);
      }
    },

    /**
     * Save previous state before applying AI changes
     * @param {string} type - Type of change: 'template', 'method', 'computed', 'watch', 'data'
     * @param {string} name - Name of the item (for method/computed/watch/data)
     * @param {string} value - The previous value to save
     */
    async savePreviousState(type, name, value) {
      if (!this.siteId) return;

      const componentId =
        type === "globalCss"
          ? null
          : this.currentComponent
            ? this.currentComponent._id
            : null;

      if (type !== "globalCss" && !componentId) return;

      // Stringify objects/arrays for backend (expects string)
      let stringValue = value;
      if (typeof value === "object" && value !== null) {
        stringValue = JSON.stringify(value);
      } else if (typeof value !== "string") {
        stringValue = String(value);
      }

      try {
        await aiRevertService.savePreviousState({
          siteId: this.siteId,
          componentId,
          type,
          name,
          value: stringValue,
          uri: this.uri,
        });
      } catch (error) {
        console.error("Error saving previous state to API:", error);
        this.$notify({
          type: "error",
          text: "Failed to save previous state for AI undo.",
        });
        throw error;
      }

      // Update reactive cache (store stringified version for consistency)
      if (type === "globalCss") {
        this.sitePreviousStates = {
          ...this.sitePreviousStates,
          globalCss: stringValue,
        };
        return;
      }

      const states = this.previousStates[componentId] || {};
      if (type === "template") {
        states.template = stringValue;
      } else {
        if (!states[type]) {
          states[type] = {};
        }
        states[type][name] = stringValue;
      }

      this.previousStates[componentId] = states;
    },

    /**
     * Get previous state for a specific item
     */
    getPreviousState(type, name = null) {
      if (type === "globalCss") {
        return this.sitePreviousStates && this.sitePreviousStates.globalCss
          ? this.sitePreviousStates.globalCss
          : null;
      }

      if (!this.currentComponent) return null;

      const componentId = this.currentComponent._id;
      const states = this.previousStates[componentId] || {};

      if (type === "template") {
        return states.template || null;
      }
      return states[type] && states[type][name] ? states[type][name] : null;
    },

    /**
     * Check if previous state exists for a specific item
     */
    hasPreviousState(type, name = null) {
      return this.getPreviousState(type, name) !== null;
    },

    /**
     * Check if a revert operation is in progress
     */
    isReverting(type = null, name = null) {
      if (this.reverting.all) return true;
      if (type && this.reverting.type === type) {
        if (name === null || this.reverting.name === name) {
          return true;
        }
      }
      return false;
    },

    /**
     * Clear previous state after revert
     */
    clearPreviousState(type, name = null) {
      if (type === "globalCss") {
        const rest = { ...(this.sitePreviousStates || {}) };
        delete rest.globalCss;
        this.sitePreviousStates = rest;
        return;
      }

      if (!this.currentComponent) return;

      const componentId = this.currentComponent._id;
      const states = this.previousStates[componentId] || {};

      if (type === "template") {
        delete states.template;
      } else if (states[type] && name) {
        delete states[type][name];
        if (Object.keys(states[type]).length === 0) {
          delete states[type];
        }
      }

      this.previousStates[componentId] = states;
    },

    /**
     * Clear all previous states for current component and global CSS
     */
    clearAllPreviousStates() {
      if (this.currentComponent) {
        const componentId = this.currentComponent._id;
        if (this.previousStates[componentId]) {
          delete this.previousStates[componentId];
        }
      }

      this.sitePreviousStates = {};
    },

    /**
     * Revert to previous state
     */
    async revertToPreviousState(type, name = null) {
      if (!this.hasPreviousState(type, name)) {
        this.$notify({
          type: "warn",
          text: "No previous state found to revert to.",
        });
        return;
      }

      const confirmMessage =
        type === "template"
          ? "Are you sure you want to revert the template to its previous state?"
          : type === "globalCss"
            ? "Are you sure you want to revert the global CSS to its previous state?"
            : `Are you sure you want to revert "${name}" to its previous state?`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      if (!this.siteId) {
        this.$notify({
          type: "error",
          text: "Unable to revert without a site context.",
        });
        return;
      }

      // Disable button immediately
      this.reverting = { type, name, all: false };
      this.$forceUpdate();

      try {
        await aiRevertService.revertPreviousState({
          siteId: this.siteId,
          componentId:
            type === "globalCss"
              ? null
              : this.currentComponent
                ? this.currentComponent._id
                : null,
          type,
          name,
          uri: this.uri,
        });

        await this.loadSitesData(this.selectedVersion);
        await this.refreshPreviousStates();

        this.$notify({
          type: "success",
          text:
            type === "template"
              ? "Template reverted successfully!"
              : type === "globalCss"
                ? "Global CSS reverted successfully!"
                : `"${name}" reverted successfully!`,
        });
      } catch (error) {
        console.error("Backend revert failed:", error);
        this.$notify({
          type: "error",
          text: "Failed to revert to previous state.",
        });
      } finally {
        // Re-enable button
        this.reverting = { type: null, name: null, all: false };
        this.$forceUpdate();
      }
    },

    /**
     * Get current previous states for the current component (for passing to child components)
     */
    getCurrentPreviousStates() {
      if (!this.currentComponent) return {};
      const componentId = this.currentComponent._id;
      return this.previousStates[componentId] || {};
    },

    /**
     * Get count of all previous states (component + site level)
     */
    getAllPreviousStatesCount() {
      let count = 0;

      // Check component-level states
      if (this.currentComponent) {
        const componentId = this.currentComponent._id;
        const states = this.previousStates[componentId] || {};

        // Count template
        if (states.template) count++;

        // Count methods
        if (states.method) {
          count += Object.keys(states.method).length;
        }

        // Count computed
        if (states.computed) {
          count += Object.keys(states.computed).length;
        }

        // Count watch
        if (states.watch) {
          count += Object.keys(states.watch).length;
        }

        // Count data
        if (states.data) {
          count += Object.keys(states.data).length;
        }
      }

      // Check site-level CSS
      if (this.hasPreviousState("globalCss")) {
        count++;
      }

      return count;
    },

    /**
     * Check if there are multiple previous states to revert
     */
    hasMultiplePreviousStates() {
      return this.getAllPreviousStatesCount() > 1;
    },

    /**
     * Revert all previous states at once
     */
    async revertAllPreviousStates() {
      const count = this.getAllPreviousStatesCount();

      if (count === 0) {
        this.$notify({
          type: "warn",
          text: "No previous states found to revert.",
        });
        return;
      }

      // Show confirmation dialog
      const confirmMessage =
        `Are you sure you want to revert all ${count} change${count > 1 ? "s" : ""} to their previous states? This will revert:\n\n` +
        this.getRevertAllSummary();

      if (!window.confirm(confirmMessage)) {
        return;
      }
      if (!this.siteId) {
        this.$notify({
          type: "error",
          text: "Unable to revert without a site context.",
        });
        return;
      }

      // Disable button immediately
      this.reverting = { type: null, name: null, all: true };
      this.$forceUpdate();

      try {
        await aiRevertService.revertAllPreviousStates({
          siteId: this.siteId,
          componentId: this.currentComponent ? this.currentComponent._id : null,
          uri: this.uri,
        });

        await this.loadSitesData(this.selectedVersion);
        await this.refreshPreviousStates();

        this.$notify({
          type: "success",
          text: `Successfully reverted ${count} change${count > 1 ? "s" : ""}!`,
        });
      } catch (error) {
        console.error("Backend revert-all failed:", error);
        this.$notify({
          type: "error",
          text: "Failed to revert all changes.",
        });
      } finally {
        // Re-enable button
        this.reverting = { type: null, name: null, all: false };
        this.$forceUpdate();
      }
    },

    /**
     * Get summary of what will be reverted
     */
    getRevertAllSummary() {
      const summary = [];

      if (this.currentComponent) {
        const componentId = this.currentComponent._id;
        const states = this.previousStates[componentId] || {};

        if (states.template) summary.push("• Template");
        if (states.method) {
          Object.keys(states.method).forEach((name) => {
            summary.push(`• Method: ${name}`);
          });
        }
        if (states.computed) {
          Object.keys(states.computed).forEach((name) => {
            summary.push(`• Computed: ${name}`);
          });
        }
        if (states.watch) {
          Object.keys(states.watch).forEach((name) => {
            summary.push(`• Watch: ${name}`);
          });
        }
        if (states.data) {
          Object.keys(states.data).forEach((name) => {
            summary.push(`• Data: ${name}`);
          });
        }
      }

      if (this.hasPreviousState("globalCss")) {
        summary.push("• Global CSS");
      }

      return summary.join("\n") || "Nothing to revert";
    },
  },
  beforeUnmount() {
    // Cleanup resize listeners
    window.removeEventListener("mousemove", this.resizePanel);
    window.removeEventListener("mouseup", this.stopResize);
  },
};
</script>
<style lang="scss" scoped>
.main-container {
  background: #f8f8ff;
  height: 100vh;
  overflow: hidden;
}
.settings-wrapper {
  width: 30%;
}
// #drag-left {
//   width: 70%;
// }

.close-sidebar {
  margin-left: -260px !important;
}
.component-container {
  background: #f8f8ff;
  flex: 1;
  overflow: auto;
  min-height: 0;
}
.component-wrapper {
  overflow: hidden !important;
  height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

/* Toggle Container */
.theme-toggle-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 6px;
}

/* Toggle */
.theme-toggle {
  position: relative;
  width: 36px; /* Slightly larger width */
  height: 16px; /* Slightly larger height */
}

.theme-toggle input {
  display: none;
}

.theme-toggle .slider {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #00aeef; /* Light blue background */
  border-radius: 16px; /* Adjusted for larger height */
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.theme-toggle input:checked + .slider {
  background-color: #2d2d2d; /* Dark background for dark mode */
}

.theme-toggle .slider:before {
  content: "";
  position: absolute;
  width: 14px; /* Larger circle size */
  height: 14px; /* Larger circle size */
  background-color: white; /* Circle color */
  border-radius: 50%;
  top: 1px; /* Centered vertically */
  left: 1px; /* Padding inside slider */
  transition: transform 0.3s ease;
}

.theme-toggle input:checked + .slider:before {
  transform: translateX(20px); /* Adjusted for larger width */
}

/* Icons */
.theme-toggle .slider .icon {
  position: absolute;
  font-size: 11px; /* Larger icon size */
  transition: opacity 0.3s ease;
}

.theme-toggle .slider .sun {
  left: 2.5px; /* Adjusted for larger size */
  top: 50%;
  transform: translateY(-50%);
  color: black; /* Sun is black */
  opacity: 1;
}

.theme-toggle input:checked + .slider .sun {
  opacity: 0;
}

.theme-toggle .slider .moon {
  right: 3px; /* Adjusted for larger size */
  top: 50%;
  transform: translateY(-50%);
  color: black; /* Moon is black */
  opacity: 0;
}

.theme-toggle input:checked + .slider .moon {
  opacity: 1;
}

/* AI Panel Styles */
.ai-resize-handle {
  width: 3px;
  background-color: #00aeef;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  height: 100%;
}

.ai-resize-handle:hover {
  background-color: #0077a8;
}

.ai-resize-handle-line {
  width: 1px;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.3);
}

.ai-panel-wrapper {
  height: 100%;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Revert Template Button */
.revert-template-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background-color: transparent;
  color: #00aeef;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 8px;
}

.revert-template-btn:hover {
  background-color: rgba(0, 174, 239, 0.1);
  color: #0088cc;
}

.revert-template-btn:active {
  opacity: 0.7;
}

.revert-template-btn i {
  font-size: 14px;
}
</style>